import React, { useState, useEffect, useMemo } from 'react';
import DoughnutGauge from './DoughnutGauge';
import BarChart from '../../../common/components/charts/BarChart';
import DashboardCard from './DashboardCard';
import Select from 'react-select';
import { useSelector } from 'react-redux';

import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { getAssessmentPeriods } from 'common/selectors/auth';

import { AggregateType } from 'common/constants/AggregateType';
interface DashboardRetentionProps {
  assessment_period_id: number;
}

const DashboardOutcomes = ({ assessment_period_id }: DashboardRetentionProps) => {
  const [outcomeScores, setOutcomeScores] = useState([]);
  const [overallScore, setOverallScore] = useState(0);

  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null);
  const [selectedOutcomeName, setSelectedOutcomeName] = useState<string>('');

  const { data } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.OUTCOME_SCORES,
    assessmentPeriodId: assessment_period_id,
  });
  const assessmentPeriods = useSelector(getAssessmentPeriods) as any[];

  const navigate = useNavigate();

  type SelectOption = { value: string; label: string };

  const contextAssessmentPeriod = Array.isArray(assessmentPeriods)
    ? assessmentPeriods.find((period: any) => period?.id?.toString?.() === assessment_period_id?.toString?.())
    : undefined;

  const availableOutcomes = useMemo<SelectOption[]>(
    () =>
      Array.isArray(contextAssessmentPeriod?.outcomes)
        ? Array.from(
            new Map<string, SelectOption>(
              contextAssessmentPeriod.outcomes
                .map((outcome: any) => ({
                  value: outcome?.id?.toString?.() || '',
                  label: outcome?.name || '',
                }))
                .filter((outcome: SelectOption) => outcome.value && outcome.label)
                .map((outcome: SelectOption): [string, SelectOption] => [outcome.value, outcome])
            ).values()
          ).sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [contextAssessmentPeriod]
  );
  const hasOutcomes = availableOutcomes.length > 0;

  useEffect(() => {
    if (availableOutcomes.length === 0) {
      setSelectedOutcome(null);
      setSelectedOutcomeName('');
      return;
    }

    const isSelectedValid = availableOutcomes.some(option => option.value === selectedOutcome);
    if (!isSelectedValid) {
      setSelectedOutcome(availableOutcomes[0].value);
      setSelectedOutcomeName(availableOutcomes[0].label);
    }
  }, [availableOutcomes, selectedOutcome]);

  useEffect(() => {
    if (!selectedOutcome) {
      setOutcomeScores([]);
      setOverallScore(0);
      return;
    }

    if (data && data.length > 0) {
      const outcomeScoreData =
        data[0]?.result?.outcomes?.find(
          (outcome: { outcome_id: string | null }) => outcome.outcome_id === selectedOutcome
        ) || [];
      const outcomeScoreOverall =
        data[0]?.result?.outcomes?.find(
          (outcome: { outcome_id: string | null }) => outcome.outcome_id === selectedOutcome
        )?.overall_score || 0;

      const scores = outcomeScoreData?.categories
        ?.filter((item: { priority: string }) => item.priority === 'primary')
        ?.map((item: { option_name: string; overall_score: number }) => ({
          label: item.option_name,
          value: parseFloat(item.overall_score.toFixed(1)),
        }))
        .sort((a: { label: any }, b: { label: string }) => a.label.localeCompare(b.label));

      setOutcomeScores(scores || []);
      setOverallScore(outcomeScoreOverall);
      return;
    }

    setOutcomeScores([]);
    setOverallScore(0);
  }, [data, selectedOutcome]);

  return (
    <DashboardCard
      title="Outcomes"
      onClick={() => navigate('/app/outcomes')}
      controls={hasOutcomes ? (
        <div onClick={e => e.stopPropagation()}>
          <Select
            options={availableOutcomes}
            placeholder="Select Outcome"
            value={availableOutcomes.find(option => option.value === selectedOutcome)}
            onChange={option => {
              setSelectedOutcome(option ? option.value : null);
              setSelectedOutcomeName(option ? option.label : '');
            }}
            styles={{
              container: base => ({
                ...base,
                width: '250px',
              }),
            }}
          />
        </div>
      ) : null}
    >
      {hasOutcomes ? (
        <>
          <CardContent>
            <GaugeWrapper>
              <DoughnutGauge max={10} complete={overallScore} precision={1} size="large" />
            </GaugeWrapper>
          </CardContent>
          <BarChart data={outcomeScores} multiple={10} percent={false} />
        </>
      ) : (
        <NoDataContainer>
          <NoDataMessage>No outcomes data is available for the selected assessment period.</NoDataMessage>
        </NoDataContainer>
      )}
    </DashboardCard>
  );
};
export default DashboardOutcomes;


const GaugeWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;


const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 240px;
  min-width: 120px;
  max-width: 240px;
  min-height: 120px;
  gap: 12px;
`;

const NoDataContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 240px;
  padding: 0 24px;
  box-sizing: border-box;
`;

const NoDataMessage = styled.div`
  text-align: center;
  max-width: 320px;
  width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
`;

